// Vídeos da galeria ("Elas usam"): o cartão (<video-card>) e o modal
// (<video-modal>).
//
// O cartão é prévia: mostra a capa e, com data-autoplay, toca mudo e em
// laço enquanto está na tela. O clique abre o vídeo no modal, com som e os
// controles do navegador. No trilho com destaque, só o cartão do centro
// abre; o clique num vizinho traz ele para o centro primeiro, e é para lá
// que o foco do teclado também leva.
//
// Nada é baixado antes de tocar: os vídeos nascem com preload="none" e a
// capa é uma imagem lazy, que sai de cena quando o primeiro quadro chega.

// Covers a transition that never fires (display: none ancestors, etc.).
const CLOSE_FALLBACK = 400;

// Plays the exit while the dialog is still open, then really closes it.
// Same helper as cart.js, header.js and quick-view.js, duplicated on
// purpose so each module stays self-contained: change one, change the
// others.
function closeDialog(dialog) {
  if (!dialog?.open || dialog.hasAttribute('closing')) return;
  dialog.setAttribute('closing', '');
  const finish = () => {
    clearTimeout(timer);
    dialog.removeEventListener('transitionend', finish);
    dialog.removeAttribute('closing');
    dialog.close();
  };
  const timer = setTimeout(finish, CLOSE_FALLBACK);
  dialog.addEventListener('transitionend', finish);
}

class VideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-video-open]');
    if (!this.video) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    // Autoplay só é permitido mudo, e a propriedade é o que o navegador
    // confere; o atributo sozinho chega tarde em cópia feita por script.
    this.video.muted = true;

    this.button?.addEventListener('click', () => this.activate(), { signal });
    this.button?.addEventListener('focus', () => {
      if (this.button.matches(':focus-visible')) this.center();
    }, { signal });
    this.video.addEventListener('playing', () => this.setAttribute('started', ''), { signal });

    // Quem pediu menos movimento vê só a capa.
    this.autoplay = this.hasAttribute('data-autoplay')
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Fora da tela a prévia pausa, então só o que se vê gasta rede.
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.preview();
    }, { threshold: 0.5 });
    this.observer.observe(this);

    // Com o modal aberto a página fica atrás do fundo escuro: as prévias
    // param, e voltam quando ele fecha.
    document.addEventListener('video-modal:toggle', () => this.preview(), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  preview() {
    const covered = document.documentElement.hasAttribute('data-video-modal-open');
    if (this.autoplay && this.inView && !covered) {
      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }
  }

  // O centro só existe no trilho com destaque, e só onde o navegador
  // anima o destaque; fora disso os cartões são iguais e todos abrem.
  isCentered() {
    const rail = this.closest('[data-rail]');
    const emphasis = this.closest('.gallery--emphasis') && CSS.supports('animation-timeline', 'view()');
    if (!rail || !emphasis) return true;
    const railBox = rail.getBoundingClientRect();
    const box = this.getBoundingClientRect();
    return Math.abs(box.left + box.width / 2 - (railBox.left + railBox.width / 2)) < box.width / 2;
  }

  center() {
    const rail = this.closest('[data-rail]');
    if (!rail || this.isCentered()) return;
    const railBox = rail.getBoundingClientRect();
    const box = this.getBoundingClientRect();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({
      left: box.left + box.width / 2 - (railBox.left + railBox.width / 2),
      behavior: reduce ? 'instant' : 'smooth',
    });
  }

  activate() {
    if (!this.isCentered()) {
      this.center();
      return;
    }
    this.closest('.gallery')?.querySelector('video-modal')?.open(this);
  }
}

class VideoModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.video = this.querySelector('video');
    if (!this.dialog || !this.video) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.querySelector('[data-video-modal-close]')?.addEventListener('click', () => this.close(), { signal });

    // Clicking the backdrop means clicking the dialog itself: it fills the
    // screen, so anything outside the video is the dialog.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    }, { signal });

    // Escape fires cancel: hold the instant close, play the exit instead.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });

    this.dialog.addEventListener('close', () => this.reset(), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.announce(false);
  }

  // As fontes do cartão, a capa grande e a proporção do vídeo. O play sai
  // dentro do clique, então o navegador deixa tocar com som.
  open(card) {
    const source = card.querySelector('video');
    if (!source) return;
    this.video.replaceChildren(...Array.from(source.querySelectorAll('source'), (node) => node.cloneNode()));
    if (card.dataset.poster) this.video.poster = card.dataset.poster;
    else this.video.removeAttribute('poster');
    this.dialog.style.setProperty('--video-ratio', card.dataset.ratio || '0.5625');
    const label = source.getAttribute('aria-label');
    if (label) this.dialog.setAttribute('aria-label', label);

    this.video.muted = false;
    this.video.load();
    this.dialog.showModal();
    this.announce(true);
    this.video.play().catch(() => {});
  }

  // O som para no clique, não no fim da animação de saída.
  close() {
    this.video.pause();
    closeDialog(this.dialog);
  }

  // Sem fontes e recarregado, o vídeo larga o download do que tocava.
  reset() {
    this.video.pause();
    this.video.replaceChildren();
    this.video.removeAttribute('poster');
    this.video.load();
    this.announce(false);
  }

  announce(open) {
    document.documentElement.toggleAttribute('data-video-modal-open', open);
    document.dispatchEvent(new CustomEvent('video-modal:toggle'));
  }
}

if (!customElements.get('video-card')) {
  customElements.define('video-card', VideoCard);
}

if (!customElements.get('video-modal')) {
  customElements.define('video-modal', VideoModal);
}
